import React, { useRef, useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { formatLikeCount, movieTitleHandler } from "../../../utils/helpers";
import { ROUTES } from "../../../constants/routes";
import { genresData } from "../../../constants/structure";

import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";

import Comments from "../../../components/icons/reactionMenu/Comments";
import LikeIcon from "../../../components/icons/chat/likeIcon";
import BookMarkIcon from "../../../components/icons/chat/bookMarkIcon";
import styles from "./MovieCarousel.module.scss";
import RoomVideoPlayer from "../../room/components/RoomVideoPlayer";
import MobileProgressController from "../../foryou/_components/MobileComponents/MobileProgressController";
import type { Swiper as SwiperClass } from "swiper";
import { setLastVisitedId } from "../../../store/slices/currentItem.slice";
import { useDispatch } from "react-redux";

interface Movie {
  id: number;
  title: string;
  title_ru: string;
  title_ua: string;
  title_ar: string;
  poster_path: string;
  backdrop_path: string;
  trailer_key?: string | null;
  release_date: string;
  vote_average: number;
  vote_count: number;
  imdb_rating?: number | null;
  rotten_tomatoes_rating?: number | null;
  genre_ids: number[];
  overview: string;
  overview_ru: string;
  overview_ua: string;
  liked: boolean;
  in_favorite: boolean;
  shares_count: number;
  favorites_count: number;
  reviews_count?: number;
  comments_count?: number;
}

interface CarouselProps {
  movies: Movie[];
  muted: boolean;
  onToggleMuted: () => void;
  likeHandler: (e: any, item: any, liked: boolean) => void;
  favouritesHandler: (e: any, item: any, isFav: boolean) => void;
  likesList: number[];
  favoritesList: number[];
  updateVoteLocal: (id: any, key: string, type: string) => void;
  isMobile: boolean;
  openLoginModal: () => void;
  onShare: (id: number) => void;
}

const IMG_BASE = process.env.REACT_APP_IMG_BASE_URL;

const MAX_SLIDES = 50;

const MovieCarouselMobile: React.FC<CarouselProps> = ({
  movies,
  muted,
  onToggleMuted,
  likeHandler,
  favouritesHandler,
  likesList,
  favoritesList,
  updateVoteLocal,
  openLoginModal,
  onShare,
}) => {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [activeIndex, setActiveIndex] = useState(0);
  const [brokenTrailerIds, setBrokenTrailerIds] = useState<Set<number>>(new Set());
  const [time, setTime] = useState(0);
  const [maxValue, setMaxValue] = useState(0);
  const mainSwiperRef = useRef<SwiperClass | null>(null);
  const thumbsSwiperRef = useRef<SwiperClass | null>(null);
  const seekRef = useRef<((seconds: number) => void) | null>(null);
  const timelinePlayer = useMemo(
    () => ({ seekTo: (seconds: number) => seekRef.current?.(seconds) }),
    [],
  );

  const visibleMovies = useMemo(
    () => movies.filter((m) => !brokenTrailerIds.has(m.id)).slice(0, MAX_SLIDES),
    [movies, brokenTrailerIds],
  );

  const syncSliders = (index: number) => {
    setActiveIndex(index);
    if (mainSwiperRef.current && mainSwiperRef.current.activeIndex !== index) {
      mainSwiperRef.current.slideTo(index);
    }
    if (thumbsSwiperRef.current && thumbsSwiperRef.current.activeIndex !== index) {
      thumbsSwiperRef.current.slideTo(index);
    }
  };

  const handleTrailerError = (movieId: number, movieIndex: number) => {
    setBrokenTrailerIds((prev) => new Set(Array.from(prev).concat(movieId)));
    if (movieIndex <= activeIndex && activeIndex > 0) {
      syncSliders(activeIndex - 1);
    }
  };

  const handleNavigateUp = () => {
    if (activeIndex > 0) syncSliders(activeIndex - 1);
  };

  const handleNavigateDown = () => {
    if (activeIndex < visibleMovies.length - 1) syncSliders(activeIndex + 1);
  };

  useEffect(() => {
    if (activeIndex > visibleMovies.length - 1) {
      setActiveIndex(Math.max(0, visibleMovies.length - 1));
    }
  }, [visibleMovies.length, activeIndex]);

  useEffect(() => {
    setTime(0);
    setMaxValue(0);
  }, [activeIndex]);

  useEffect(() => {
    const sw = thumbsSwiperRef.current;
    if (!sw) return;
    sw.update();
    sw.slideTo(activeIndex);
    const id = window.setTimeout(() => {
      sw.update();
      sw.slideTo(activeIndex);
    }, 320);
    return () => window.clearTimeout(id);
  }, [activeIndex, visibleMovies.length]);

  const paginationDots = useMemo(() => {
    const total = visibleMovies.length;
    const maxVisible = 5;
    const start = Math.max(0, Math.min(activeIndex - 2, Math.max(0, total - maxVisible)));
    const end = Math.min(total, start + maxVisible);
    const dots: number[] = [];
    for (let i = start; i < end; i++) dots.push(i);
    return dots;
  }, [visibleMovies.length, activeIndex]);

  const formatCount = (value?: number) => {
    const count = value || 0;
    if (count >= 1000) return `${Math.round(count / 1000)}K`;
    return count;
  };

  const activeMovie = visibleMovies[activeIndex];
  const activeLiked = activeMovie ? likesList?.includes(activeMovie.id) : false;
  const activeFav = activeMovie ? favoritesList?.includes(activeMovie.id) : false;
  const activeComments = activeMovie
    ? activeMovie.reviews_count ?? activeMovie.comments_count ?? activeMovie.shares_count ?? 0
    : 0;
  const activeTitle = activeMovie ? movieTitleHandler(i18n.language, activeMovie) || activeMovie.title : "";
  const activeYear = activeMovie?.release_date?.substring(0, 4);
  const activeGenres = activeMovie
    ? activeMovie.genre_ids
        ?.slice(0, 2)
        .map((gid) => genresData.find((g) => g.id === gid)?.name)
        .filter(Boolean)
        .map((name) => t(name!))
        .join(", ")
    : "";

  const goToDetails = (id: number) => {
    navigate(`${ROUTES.DETAILS}/${id}`);
    dispatch(setLastVisitedId(id as number));
  };

  const handleLike = (e: any) => {
    e.stopPropagation();
    if (!activeMovie) return;
    const token = localStorage.getItem("accessToken");
    if (!token) return openLoginModal();
    likeHandler(e, activeMovie, activeLiked);
    updateVoteLocal(activeMovie.id, "vote_count", activeLiked ? "decrement" : "increment");
  };

  const handleSaved = (e: any) => {
    e.stopPropagation();
    if (!activeMovie) return;
    const token = localStorage.getItem("accessToken");
    if (!token) return openLoginModal();
    favouritesHandler(e, activeMovie, activeFav);
    updateVoteLocal(activeMovie.id, "favorites_count", activeFav ? "decrement" : "increment");
  };

  if (!visibleMovies.length) return null;

  return (
    <div className={styles.mobileCarousel}>
      {activeMovie && (
        <div className={styles.mobileMovieInfo} onClick={() => goToDetails(activeMovie.id)}>
          {activeMovie.poster_path ? (
            <div className={styles.mobileInfoPoster}>
              <img
                src={`${IMG_BASE}${activeMovie.poster_path}`}
                alt={activeTitle}
              />
            </div>
          ) : (
            <div className={styles.mobileInfoPosterPlaceholder} />
          )}
          <div className={styles.mobileInfoText}>
            <h4 className={styles.mobileInfoTitle}>{activeTitle}</h4>
            <div className={styles.mobileInfoMeta}>
              {activeYear && <span>{activeYear}</span>}
            </div>
            {activeGenres && <div className={styles.mobileInfoGenre}>{activeGenres}</div>}
          </div>
        </div>
      )}

      <div className={styles.mobilePlayerArea}>
        <div className={styles.mobileFrames} aria-hidden>
          <div className={`${styles.mobileFrameBlock} ${styles.mobileFrameFar}`} />
          <div className={`${styles.mobileFrameBlock} ${styles.mobileFrameNear}`} />
        </div>

        <Swiper
          direction="vertical"
          slidesPerView={1}
          grabCursor
          className={styles.mobileMainSwiper}
          onSwiper={(swiper: SwiperClass) => {
            mainSwiperRef.current = swiper;
          }}
          onSlideChange={(swiper: SwiperClass) => syncSliders(swiper.activeIndex)}
        >
          {visibleMovies.map((movie, index) => (
            <SwiperSlide key={movie.id} className={styles.mobileMainSlide}>
              <div className={styles.mobileBackdrop}>
                {movie.backdrop_path && (
                  <img src={`${IMG_BASE}${movie.backdrop_path}`} alt="" loading="lazy" />
                )}
              </div>
              {index === activeIndex && (
                <div className={styles.mobileVideo}>
                  <RoomVideoPlayer
                    trailerKey={movie.trailer_key ?? null}
                    backdropPath={movie.backdrop_path}
                    muted={muted}
                    onToggleMuted={onToggleMuted}
                    isActive={true}
                    onTrailerError={() => handleTrailerError(movie.id, index)}
                    carouselMode
                    reportProgress={setTime}
                    reportDuration={setMaxValue}
                    registerSeek={(fn) => {
                      seekRef.current = fn;
                    }}
                    navigation={{
                      mode: "slides",
                      onNavigateUp: handleNavigateUp,
                      onNavigateDown: handleNavigateDown,
                      canNavigateUp: activeIndex > 0,
                      canNavigateDown: activeIndex < visibleMovies.length - 1,
                    }}
                  />
                </div>
              )}
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {activeMovie && (
        <div className={styles.mobileActionsBar}>
          <div className={styles.mobileActions}>
            <div className={styles.mobileActionsItem} onClick={handleLike}>
              <div
                className={`${styles.mobileActionsIcon} ${activeLiked ? styles.mobileActionsIconActive : ""}`}
              >
                <LikeIcon />
              </div>
              <span className={styles.mobileActionsValue}>{formatCount(activeMovie.vote_count)}</span>
            </div>

            <div className={styles.mobileActionsItem} onClick={() => goToDetails(activeMovie.id)}>
              <div className={styles.mobileActionsIcon}>
                <Comments />
              </div>
              <span className={styles.mobileActionsValue}>{formatCount(activeComments)}</span>
            </div>

            <div className={styles.mobileActionsItem} onClick={handleSaved}>
              <div
                className={`${styles.mobileActionsIcon} ${activeFav ? styles.mobileActionsIconActive : ""}`}
              >
                <BookMarkIcon />
              </div>
              <span className={styles.mobileActionsValue}>
                {formatLikeCount(activeMovie.favorites_count, { compact: true })}
              </span>
            </div>
          </div>

          <button className={styles.mobileSearchBtn} onClick={() => goToDetails(activeMovie.id)}>
            {t("chat_search_movie", "Search movie")}
          </button>
        </div>
      )}

      <Swiper
        modules={[]}
        slidesPerView="auto"
        spaceBetween={6}
        edgeSwipeDetection={true}
        edgeSwipeThreshold={40}
        touchStartPreventDefault={false}
        className={styles.mobileThumbs}
        onSwiper={(swiper: SwiperClass) => {
          thumbsSwiperRef.current = swiper;
        }}
      >
        {visibleMovies.map((movie, index) => {
          const title = movieTitleHandler(i18n.language, movie) || movie.title;
          const isCurrent = index === activeIndex;

          return (
            <SwiperSlide
              key={movie.id}
              className={`${styles.mobileThumbSlide} ${isCurrent ? styles.mobileThumbSlideActive : ""}`}
              onClick={() => {
                if (isCurrent) {
                  goToDetails(movie.id);
                } else {
                  syncSliders(index);
                }
              }}
            >
              {movie.poster_path ? (
                <div className={styles.mobileThumbPoster}>
                  <img src={`${IMG_BASE}${movie.poster_path}`} alt={title} />
                </div>
              ) : (
                <div className={styles.mobileThumbPosterPlaceholder} />
              )}
            </SwiperSlide>
          );
        })}
      </Swiper>

      <div className={styles.mobilePagination}>
        {paginationDots.map((i) => (
          <span
            key={i}
            className={`${styles.mobilePaginationDot} ${i === activeIndex ? styles.mobilePaginationDotActive : ""}`}
            onClick={() => syncSliders(i)}
          />
        ))}
      </div>

      <div className={styles.mobileTimeline}>
        <MobileProgressController
          maxValue={maxValue}
          time={time}
          player={timelinePlayer}
          setTime={setTime}
          isMobile={true}
        />
      </div>
    </div>
  );
};

export default MovieCarouselMobile;
